import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import Button from "../UIComponents/Button";
import { RiArrowRightUpLine } from "react-icons/ri";


const HeroSection = () => {
    const introRef = useRef(null);
    const titleRef = useRef(null);
    const descRef = useRef(null);
    const ctaRef = useRef(null);
    const glowRef = useRef(null);
    const tlRef = useRef(null);

    useEffect(() => {
        if (!gsap) return;

        // Ensure refs exist
        if (!introRef.current || !titleRef.current || !descRef.current || !ctaRef.current) return;

        if (tlRef.current) tlRef.current.kill();

        const tl = gsap.timeline();
        tlRef.current = tl;

        tl.fromTo(
            introRef.current,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
        )
            .fromTo(
                titleRef.current.children,
                { opacity: 0, y: 50, rotationX: -15 },
                { opacity: 1, y: 0, rotationX: 0, duration: 1.2, stagger: 0.15, ease: "power3.out" },
                "-=0.4"
            )
            .fromTo(
                descRef.current,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.9, ease: "power2.out" },
                "-=0.6"
            )
            .fromTo(
                [ctaRef.current],
                { opacity: 0 },
                { opacity: 1, duration: 0.8, ease: "power2.out" },
                "-=0.6"
            );

        if (glowRef.current) {
            gsap.to(glowRef.current, { y: -20, x: 10, duration: 4, repeat: -1, yoyo: true, ease: "sine.inOut" });
        }

        return () => tlRef.current && tlRef.current.kill();
    }, []);

    return (
        <section
            className="relative min-h-screen bg-background flex items-center px-6 md:px-12 lg:px-20 xl:px-32 overflow-hidden md:pt-0"
            aria-labelledby="hero-title"
            style={{ borderBottom: "1px solid #acacac2b" }}
            id="home"
        >
            <div
                ref={glowRef}
                className="absolute top-20 right-10 md:right-15 w-100 h-80 md:w-[700px] md:h-[400px] bg-primary/20 blur-[120px] rounded-full pointer-events-none"
            ></div>

            <div className="absolute bottom-10 left-0 w-72 h-72 md:w-96 md:h-96 bg-accent/15 blur-[100px] rounded-full pointer-events-none animate-pulse"></div>

            <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f30_1px,transparent_2px),linear-gradient(to_bottom,#4f4f4f30_1px,transparent_2px)] bg-[size:5rem_5rem] pointer-events-none"></div>

            <div className="max-w-6xl w-full relative z-10 pt-5 sm:pt-12">

                {/* INTRO */}
                <p
                    ref={introRef}
                    className="mb-8 2xl:mb-10 text-accent text-sm md:text-base 2xl:text-2xl font-semibold tracking-wider uppercase flex items-center gap-2"
                >
                    <span className="w-8 h-[2px] bg-accent hidden sm:inline-flex"></span>
                    Bhavya Kansara
                </p>

                <h1 class="sr-only">
                    Bhavya Kansara – Web Developer & Graphic Designer
                </h1>
                {/* MAIN TITLE */}
                <h1
                    id="hero-title"
                    ref={titleRef}
                    className="text-text-light font-extrabold text-3xl md:text-6xl lg:text-7xl
        2xl:text-8xl leading-[1.1] mb-6 2xl:mb-10"
                >
                    <span className="block">Web Developer</span>
                    <span className="block">& Graphic Designer</span>
                </h1>
                <p className="sr-only">
                    Bhavya Kansara is a web developer and graphic designer creating
                    clean, user-focused digital experiences.
                </p>
                {/* DESCRIPTION */}
                <p
                    ref={descRef}
                    className="text-text-light/75 text-base md:text-xl max-w-2xl leading-relaxed mb-10 2xl:mb-12"
                >
                    I design and develop websites with a focus on clean visuals,
                    usability, and clear user journeys.
                </p>


                {/* CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4">
                        <Button
                            type="primary"
                            text="Hire Me"
                            icon={<RiArrowRightUpLine className="ArrowUp" />}
                            onClick={() => {
                                document.getElementById("contact")?.scrollIntoView({
                                    behavior: "smooth",
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );

};

export default HeroSection;
